// ---------------------------------------------------------------------------
// VectorClient — REST-based abstraction over Pinecone / Chroma for
// semantic search and duplicate detection of SysML requirements.
// ---------------------------------------------------------------------------

import type { VectorDbConfig, VectorMetadata } from './ingestion-types.js';
import { EmbeddingClient } from './embedding-client.js';

/** A single vector query result */
export interface VectorSearchResult {
  id: string;
  score: number;
  metadata: VectorMetadata;
}

export class VectorClient {
  private config: VectorDbConfig | null = null;
  private apiKey: string | null = null;
  private embedding: EmbeddingClient;

  constructor(embeddingClient: EmbeddingClient) {
    this.embedding = embeddingClient;
  }

  /** Configure the vector DB connection */
  configure(config: VectorDbConfig, apiKey: string): void {
    this.config = config;
    this.apiKey = apiKey;
  }

  get isConfigured(): boolean {
    return this.config !== null && this.apiKey !== null && this.embedding.isConfigured;
  }

  // ════════════════════════════════════════════════════════════════════════
  // Upsert requirements into the vector DB
  // ════════════════════════════════════════════════════════════════════════

  async upsertRequirements(
    requirements: Array<{
      id: string;
      name: string;
      text: string;
      priority: string;
      source?: string;
      documentId: string;
    }>,
  ): Promise<void> {
    this._assertConfigured();
    if (requirements.length === 0) return;

    // Embed all requirement texts
    const texts = requirements.map((r) => `${r.name}: ${r.text}`);
    const embeddings = await this.embedding.embed(texts);

    const now = new Date().toISOString();
    const vectors = requirements.map((r, i) => ({
      id: r.id,
      values: embeddings[i],
      metadata: {
        documentId: r.documentId,
        requirementName: r.name,
        text: r.text,
        priority: r.priority,
        source: r.source ?? '',
        ingestedAt: now,
      } satisfies VectorMetadata,
    }));

    if (this.config!.provider === 'pinecone') {
      await this._pineconeUpsert(vectors);
    } else {
      await this._chromaUpsert(vectors);
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Find similar requirements (for duplicate detection)
  // ════════════════════════════════════════════════════════════════════════

  async findSimilar(
    text: string,
    topK = 5,
    threshold = 0.85,
  ): Promise<VectorSearchResult[]> {
    this._assertConfigured();

    const embedding = await this.embedding.embedOne(text);

    let results: VectorSearchResult[];
    if (this.config!.provider === 'pinecone') {
      results = await this._pineconeQuery(embedding, topK);
    } else {
      results = await this._chromaQuery(embedding, topK);
    }

    return results.filter((r) => r.score >= threshold);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Semantic search across all stored requirements
  // ════════════════════════════════════════════════════════════════════════

  async search(query: string, topK = 10): Promise<VectorSearchResult[]> {
    this._assertConfigured();

    const embedding = await this.embedding.embedOne(query);

    if (this.config!.provider === 'pinecone') {
      return this._pineconeQuery(embedding, topK);
    }
    return this._chromaQuery(embedding, topK);
  }

  // ════════════════════════════════════════════════════════════════════════
  // Test connection
  // ════════════════════════════════════════════════════════════════════════

  async testConnection(): Promise<{ ok: boolean; message: string }> {
    if (!this.config || !this.apiKey) {
      return { ok: false, message: 'Vector DB not configured' };
    }

    try {
      if (this.config.provider === 'pinecone') {
        const response = await fetch(
          `${this.config.endpoint}/describe_index_stats`,
          {
            method: 'POST',
            headers: this._pineconeHeaders(),
            body: JSON.stringify({}),
          },
        );
        if (!response.ok) {
          return { ok: false, message: `Pinecone error: ${response.status}` };
        }
        return { ok: true, message: 'Pinecone connected' };
      }

      // Chroma
      const response = await fetch(`${this.config.endpoint}/api/v1/heartbeat`);
      if (!response.ok) {
        return { ok: false, message: `Chroma error: ${response.status}` };
      }
      return { ok: true, message: 'Chroma connected' };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false, message: `Connection failed: ${msg}` };
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // Pinecone REST implementation
  // ════════════════════════════════════════════════════════════════════════

  private _pineconeHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Api-Key': this.apiKey!,
    };
  }

  private async _pineconeUpsert(
    vectors: Array<{ id: string; values: number[]; metadata: VectorMetadata }>,
  ): Promise<void> {
    const body: Record<string, unknown> = { vectors };
    if (this.config!.namespace) {
      body.namespace = this.config!.namespace;
    }

    const response = await fetch(`${this.config!.endpoint}/vectors/upsert`, {
      method: 'POST',
      headers: this._pineconeHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Pinecone upsert failed (${response.status}): ${text}`);
    }
  }

  private async _pineconeQuery(
    vector: number[],
    topK: number,
  ): Promise<VectorSearchResult[]> {
    const body: Record<string, unknown> = {
      vector,
      topK,
      includeMetadata: true,
    };
    if (this.config!.namespace) {
      body.namespace = this.config!.namespace;
    }

    const response = await fetch(`${this.config!.endpoint}/query`, {
      method: 'POST',
      headers: this._pineconeHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Pinecone query failed (${response.status}): ${text}`);
    }

    const json = (await response.json()) as {
      matches: Array<{
        id: string;
        score: number;
        metadata?: VectorMetadata;
      }>;
    };

    return (json.matches ?? []).map((m) => ({
      id: m.id,
      score: m.score,
      metadata: m.metadata ?? {
        documentId: '',
        requirementName: '',
        text: '',
        priority: '',
        source: '',
        ingestedAt: '',
      },
    }));
  }

  // ════════════════════════════════════════════════════════════════════════
  // Chroma REST implementation
  // ════════════════════════════════════════════════════════════════════════

  private async _chromaUpsert(
    vectors: Array<{ id: string; values: number[]; metadata: VectorMetadata }>,
  ): Promise<void> {
    const baseUrl = this.config!.endpoint;
    const collectionName = this.config!.indexName;

    // Get or create collection
    const createResp = await fetch(`${baseUrl}/api/v1/collections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: collectionName,
        get_or_create: true,
      }),
    });

    if (!createResp.ok) {
      const text = await createResp.text();
      throw new Error(`Chroma collection error (${createResp.status}): ${text}`);
    }

    const collection = (await createResp.json()) as { id: string };

    // Upsert into the collection
    const response = await fetch(
      `${baseUrl}/api/v1/collections/${collection.id}/upsert`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: vectors.map((v) => v.id),
          embeddings: vectors.map((v) => v.values),
          metadatas: vectors.map((v) => v.metadata as unknown as Record<string, string>),
          documents: vectors.map((v) => v.metadata.text),
        }),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Chroma upsert failed (${response.status}): ${text}`);
    }
  }

  private async _chromaQuery(
    vector: number[],
    topK: number,
  ): Promise<VectorSearchResult[]> {
    const baseUrl = this.config!.endpoint;
    const collectionName = this.config!.indexName;

    // Get collection
    const colResp = await fetch(
      `${baseUrl}/api/v1/collections/${collectionName}`,
    );
    if (!colResp.ok) return [];

    const collection = (await colResp.json()) as { id: string };

    const response = await fetch(
      `${baseUrl}/api/v1/collections/${collection.id}/query`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query_embeddings: [vector],
          n_results: topK,
          include: ['metadatas', 'distances'],
        }),
      },
    );

    if (!response.ok) return [];

    const json = (await response.json()) as {
      ids: string[][];
      distances: number[][];
      metadatas: Array<Array<Record<string, string>>>;
    };

    if (!json.ids?.[0]) return [];

    return json.ids[0].map((id, i) => ({
      id,
      // Chroma returns distances; convert to similarity (1 - distance for cosine)
      score: 1 - (json.distances?.[0]?.[i] ?? 0),
      metadata: (json.metadatas?.[0]?.[i] ?? {
        documentId: '',
        requirementName: '',
        text: '',
        priority: '',
        source: '',
        ingestedAt: '',
      }) as unknown as VectorMetadata,
    }));
  }

  // ════════════════════════════════════════════════════════════════════════
  // Internal
  // ════════════════════════════════════════════════════════════════════════

  private _assertConfigured(): void {
    if (!this.config || !this.apiKey) {
      throw new Error('VectorClient not configured. Call configure(config, apiKey) first.');
    }
    if (!this.embedding.isConfigured) {
      throw new Error('EmbeddingClient not configured. Configure embeddings first.');
    }
  }
}
